import React, { useRef, useState } from "react";
import Axios from "axios";
import { FiUpload } from "react-icons/fi";
import { ButtonSecondary, Label } from "../globals";
import { ButtonDelete } from "../common/Buttons";
import ModalSimple from "../Layout/Modals/ModalSimple";
import {
  antdMessageError,
  antdMessageSuccess,
  customAntdMessage,
} from "../../helpers/Utils";

const IMPORT_CONFIG = {
  freeze: {
    title: "Import freeze prices",
    description: "Import freeze prices for retail products from an Excel file.",
    endpoint: "/api/v1/retail_products/import_freeze_prices",
    requiredColumnsText: "Required columns: SKU, price, date_from, date_to. Optional column: department.",
    samples: [
      { href: "/files/retail_price_import_sample.xlsx", label: "Download sample (all departments)" },
      { href: "/files/retail_price_import_with_department_sample.xlsx", label: "Download sample (with department)" },
    ],
  },
  discount: {
    title: "Import discount prices",
    description: "Import discount prices for retail products from an Excel file.",
    endpoint: "/api/v1/retail_products/import_discount_prices",
    requiredColumnsText: "Required columns: SKU, price, date_from, date_to. Optional column: department.",
    samples: [
      { href: "/files/retail_price_import_sample.xlsx", label: "Download sample (all departments)" },
      { href: "/files/retail_price_import_with_department_sample.xlsx", label: "Download sample (with department)" },
    ],
  },
  rrp: {
    title: "Import RRP prices",
    description: "Import RRP prices for retail products from an Excel file.",
    endpoint: "/api/v1/retail_products/import_rrp_prices",
    requiredColumnsText: "Required columns: SKU, RRP.",
    samples: [
      { href: "/files/retail_rrp_price_import_sample.xlsx", label: "Download sample (SKU and RRP)" },
    ],
  },
};

const EXCEL_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const RetailPriceImport = ({ onClose, importType = "freeze" }) => {
  const fileRef = useRef();
  const [documents, setDocuments] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const config = IMPORT_CONFIG[importType] || IMPORT_CONFIG.freeze;

  const showCustomErrorMessage = (errorMessage) => {
    customAntdMessage(
      "Errors in Uploaded File",
      errorMessage || "Error occurred",
      "error"
    );
  };

  const addFile = async (file) => {
    const isXlsx = file?.type === EXCEL_MIME || file?.name?.toLowerCase().endsWith(".xlsx");
    if (!isXlsx) {
      antdMessageError("Please upload excel file");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    const { data: total } = await Axios.post(
      "/api/v1/retail_products/count_price_import_rows",
      formData
    );

    setDocuments((prev) => [
      ...prev,
      {
        total,
        file,
      },
    ]);
  };

  const submit = async () => {
    try {
      setSubmitting(true);
      const formData = new FormData();
      documents.forEach(({ file }) => {
        formData.append("files[]", file);
      });

      const { data } = await Axios.post(config.endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const errorsCount = data?.errors?.length || 0;
      const message = `Updated ${data?.updated || 0} price records. Skipped ${data?.skipped || 0} rows.`;
      if (errorsCount > 0) {
        customAntdMessage("Import completed with warnings", message, "warning");
      } else {
        antdMessageSuccess(message);
      }
      onClose();
    } catch (error) {
      showCustomErrorMessage(error?.response?.data?.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalSimple text={config.title} hide={true} onClose={onClose}>
      <p style={{ color: "#1D1D1D", marginTop: 0, marginBottom: 10 }}>
        {config.description}
      </p>
      <p style={{ color: "#595959", marginTop: 0, marginBottom: 10, fontSize: 12 }}>
        {config.requiredColumnsText}
      </p>
      {config.samples?.length > 0 && (
        <div className="d-flex align-items-center" style={{ gap: 12, marginBottom: 12 }}>
          {config.samples.map((sample) => (
            <a key={sample.href} href={sample.href} download>
              {sample.label}
            </a>
          ))}
        </div>
      )}
      <input
        hidden={true}
        ref={fileRef}
        type="file"
        onChange={async (e) => {
          const file = e.target.files[0];
          if (!file) return;

          try {
            await addFile(file);
          } catch (error) {
            showCustomErrorMessage(error?.response?.data?.message);
          }
        }}
      />

      <Label required={true}>Price import file</Label>
      <div
        style={{
          width: "100%",
          border: "1px dashed #d9d9d9",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "25px 15px",
          borderRadius: "2px",
          background: "#fafafa",
          cursor: "pointer",
          WebkitTransition: "border-color 0.3s",
          transition: "border-color 0.3s",
        }}
        onClick={() => fileRef.current.click()}
      >
        <div className="ant-upload-text">
          <FiUpload className="mr-2" /> Upload the file here.
        </div>
      </div>

      {documents.map(({ total, file }) => (
        <div
          key={file.lastModified}
          className="px-3 py-2 mb-4 mt-2"
          style={{
            border: "1px solid #D9D9D9",
            borderRadius: 4,
          }}
        >
          <div>
            <span className="font-medium">{total}</span> rows will be processed
          </div>
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <span className="text-decoration-underline pl-1">{file.name}</span>
            </div>
            <ButtonDelete
              onDelete={() =>
                setDocuments((prev) =>
                  prev.filter((s) => s.file.lastModified !== file.lastModified)
                )
              }
            />
          </div>
        </div>
      ))}

      <div style={{ height: 40 }}>
        <ButtonSecondary
          loading={submitting}
          disabled={documents.length === 0}
          onClick={submit}
        >
          Start import
        </ButtonSecondary>
      </div>
    </ModalSimple>
  );
};

export default RetailPriceImport;
